import { useMemo } from "react";
import { AutovioCalendarEvent } from "../model/autovioCalendarEvents";
import { WorktimeStatsDto2 } from "../api/server.api";
import { useNegativeBalanceWarnings } from "./useNegativeBalanceWarnings";
import { useMissingVehiclesWarnings } from "./useMissingVehiclesWarnings";

export function useInstructorCalendarWarnings(
  events: undefined | AutovioCalendarEvent[],
  worktimeStats: undefined | null | WorktimeStatsDto2,
): {
  warningsByDay: Map</* "yyyy-MM-dd" */ string, Array<string>>;
  warningsByEventId: Map<string, Array<string>>;
} {
  const missingVehiclesWarnings = useMissingVehiclesWarnings(events);
  const negativeBalanceWarnings = useNegativeBalanceWarnings(events);
  const warningsByDay = useMemo(() => {
    const warningsByDay = new Map<string, Array<string>>();
    const { violations, potentialViolations } = worktimeStats?.data ?? {};
    for (const [
      day,
      {
        tooLittlePauses,
        tooLittleRest,
        tooLongWorkday,
        tooManyLessons,
        tooLittlePausesMinutes,
        tooLittleRestMinutes,
        tooLongWorkdayMinutes,
        tooManyLessonsMinutes,
      },
    ] of Object.entries(violations ?? {})) {
      const warnings: Array<string> = [];
      if (tooLittlePauses) {
        warnings.push(
          `${_tooLittlePausesWarning}${tooLittlePausesMinutes ? ` (${tooLittlePausesMinutes} Minuten)` : ""}`,
        );
      }
      if (tooLittleRest) {
        warnings.push(`${_tooLittleRestWarning}${tooLittleRestMinutes ? ` (${tooLittleRestMinutes} Minuten)` : ""}`);
      }
      if (tooLongWorkday) {
        warnings.push(`${_tooLongWorkdayWarning}${tooLongWorkdayMinutes ? ` (${tooLongWorkdayMinutes} Minuten)` : ""}`);
      }
      if (tooManyLessons) {
        warnings.push(`${_tooManyLessonsWarning}${tooManyLessonsMinutes ? ` (${tooManyLessonsMinutes} Minuten)` : ""}`);
      }
      if (warnings.length > 0) {
        warningsByDay.set(day, warnings);
      }
    }
    for (const [
      day,
      {
        tooLittlePauses,
        tooLittleRest,
        tooLongWorkday,
        tooManyLessons,
        tooLittlePausesMinutes,
        tooLittleRestMinutes,
        tooLongWorkdayMinutes,
        tooManyLessonsMinutes,
      },
    ] of Object.entries(potentialViolations ?? {})) {
      // Ignore potential violations if there are already real violations for the day ...
      if (warningsByDay.has(day)) {
        continue;
      }
      const warnings: Array<string> = [];
      if (tooLittlePauses) {
        warnings.push(
          `${_tooLittlePausesWarning}${tooLittlePausesMinutes ? ` (${tooLittlePausesMinutes} Minuten)` : ""}`,
        );
      }
      if (tooLittleRest) {
        warnings.push(`${_tooLittleRestWarning}${tooLittleRestMinutes ? ` (${tooLittleRestMinutes} Minuten)` : ""}`);
      }
      if (tooLongWorkday) {
        warnings.push(`${_tooLongWorkdayWarning}${tooLongWorkdayMinutes ? ` (${tooLongWorkdayMinutes} Minuten)` : ""}`);
      }
      if (tooManyLessons) {
        warnings.push(`${_tooManyLessonsWarning}${tooManyLessonsMinutes ? ` (${tooManyLessonsMinutes} Minuten)` : ""}`);
      }
      if (warnings.length > 0) {
        warningsByDay.set(day, warnings);
      }
    }
    for (const [day, warning] of missingVehiclesWarnings.warningsByDay.entries()) {
      const warnings = warningsByDay.get(day);
      if (warnings) {
        warnings.push(warning);
      } else {
        warningsByDay.set(day, [warning]);
      }
    }
    for (const [day, warning] of negativeBalanceWarnings.warningsByDay.entries()) {
      const warnings = warningsByDay.get(day);
      if (warnings) {
        warnings.push(warning);
      } else {
        warningsByDay.set(day, [warning]);
      }
    }
    return warningsByDay;
  }, [missingVehiclesWarnings, negativeBalanceWarnings, worktimeStats]);
  const warningsByEventId = useMemo(() => {
    const warningsByEventId = new Map<string, Array<string>>();
    for (const [eventId, warnings] of missingVehiclesWarnings.warningsByEventId.entries()) {
      warningsByEventId.set(eventId, warnings);
    }
    for (const [eventId, warning] of negativeBalanceWarnings.warningsByEventId.entries()) {
      warningsByEventId.set(eventId, [...(warningsByEventId.get(eventId) ?? []), warning]);
    }
    return warningsByEventId;
  }, [missingVehiclesWarnings, negativeBalanceWarnings]);
  return { warningsByDay, warningsByEventId };
}

const _tooLittlePausesWarning = "Nicht genug Pausen eingeplant";
const _tooLittleRestWarning = "Weniger als 11 Stunden Ruhezeit";
const _tooLongWorkdayWarning = "Mehr als 10 Arbeitsstunden";
const _tooManyLessonsWarning = "Mehr als 11 Fahrstunden";
